import { type MouseEvent, useState } from 'react';
import {
  CardActionArea,
  Collapse,
  Divider,
  Grid,
  Skeleton,
  styled,
} from '@mui/material';

import CardContent from '@mui/material/CardContent';
import { ErrorBoundary } from 'react-error-boundary';
import {
  bronzeAwardStyle,
  CardPrimaryIconButton,
  NoAwardStyle,
  silverAwardStyle,
  StyledCard,
} from './AssureAppCard.styles';
import { useAppStatusProps, useShouldHideCost } from './AssureAppCard.hooks';

import { BenefitsStatusIcon } from '../../../BenefitsStatusIcon';
import { CardFooterCollapsible } from '../../CardFooterCollapsible';
import { CardFooterOverlay } from '../../CardFooterOverlay';
import { ContainedText } from '../../../ContainedText';
import type { AppCardProps } from '../../AppCardTypes.types';
import { CardCategories, CardScreenshots } from '../Shared';
import { AssureAppCardHeader } from './Sections/AssureCardHeader';
import DownloadIcon from '@mui/icons-material/Download';
import IosShareIcon from '@mui/icons-material/IosShare';
import { AppPlatformInfo } from '../../AppPlatformInfo';
import { AppPlatformImageInfo } from '../../AppPlatformImageInfo';

export const ShieldImage = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(-1.5),
  right: theme.spacing(-2),
  width: '50px',
  height: '56px',
  borderRadius: '4px',
  alignItems: 'center',
  filter: 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.25))',
}));

/**
 * Props for configuring an Assure app card component from [MicroLibraryCoreUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryCoreUI)
 *
 * @property {string} appImages - The images to display in the card.
 * @property {string} avatarImage - The image to display in the card header.
 * @property {CardHeaderProps} CardHeaderProps - The props for the card header.
 */
export const AssureAppCard = ({
  appImages,
  avatarImage,
  CardProps,
  categories,
  cost,
  isDefaultOpen,
  onClick,
  platforms,
  recommendButton,
  downloadButton,
  title,
  translations,
  appStatus,
  isLoading,
  assured,
  appPlatformType,
  platformConfiguration,
  FlexibleImageComponent,
}: AppCardProps) => {
  const appStatusProps = useAppStatusProps(appStatus, translations);

  const handleRecommendButtonClick = (
    event: MouseEvent<HTMLElement>,
    callback: () => void
  ) => {
    event.stopPropagation();
    callback();
  };

  const handleDownloadButtonClick = (
    event: MouseEvent<HTMLElement>,
    callback: () => void
  ) => {
    event.stopPropagation();
    callback();
  };

  const [isCardOpen, setIsCardOpen] = useState(isDefaultOpen ?? false);
  const handleOpenClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setIsCardOpen(!isCardOpen);
  };

  const shouldHideCost = useShouldHideCost(appStatus);

  const getStyle = () => {
    switch (assured?.type) {
      case 'bronze':
        return bronzeAwardStyle;
      case 'silver':
        return silverAwardStyle;
      default:
        return NoAwardStyle;
    }
  };
  const style = getStyle();

  return (
    <div style={{ width: '330px', height: '360px', position: 'relative' }}>
      <StyledCard
        sx={{
          ...(onClick && {
            cursor: 'pointer',
          }),
        }}
        style={{ position: 'relative' }}
        raised
        onClick={onClick}
        data-testid="app-card"
        {...CardProps}
      >
        <CardActionArea component="div" sx={{ height: '360px' }}>
          <AssureAppCardHeader
            appName={title}
            avatarImage={avatarImage}
            background={style.background}
          />
          <CardContent
            sx={{
              padding: ' 10px 16px 10px 16px',
              background: style.subHeaderBackground,
            }}
          >
            <Grid container>
              <Grid item sx={{ width: '85%' }}>
                <CardCategories categories={categories} isLoading={isLoading} />
              </Grid>
              {downloadButton && (
                <Grid item container sx={{ width: '15%' }}>
                  <CardPrimaryIconButton
                    onMouseDown={(event) => event.stopPropagation()}
                    onTouchStart={(event) => event.stopPropagation()}
                    onClick={(event) =>
                      handleDownloadButtonClick(event, downloadButton.onClick)
                    }
                    title={downloadButton.label}
                    data-testid="app-card-download-button"
                  >
                    <DownloadIcon />
                  </CardPrimaryIconButton>
                </Grid>
              )}
              {recommendButton && (
                <Grid item container>
                  <CardPrimaryIconButton
                    onMouseDown={(event) => event.stopPropagation()}
                    onTouchStart={(event) => event.stopPropagation()}
                    onClick={(event) =>
                      handleRecommendButtonClick(event, recommendButton.onClick)
                    }
                    title={recommendButton.label}
                    data-testid="app-card-download-button"
                  >
                    <IosShareIcon />
                  </CardPrimaryIconButton>
                </Grid>
              )}
            </Grid>
          </CardContent>
          <Divider light />
          <Collapse
            orientation="vertical"
            in={isCardOpen}
            data-testid="app-card-content-collapsible"
          >
            <CardContent>
              <Grid container spacing={2}>
                {appStatusProps && !isLoading && (
                  <Grid item data-testid="app-card-app-status">
                    <BenefitsStatusIcon
                      status={appStatusProps.status}
                      label={appStatusProps.costType}
                    />
                  </Grid>
                )}

                {cost && !shouldHideCost && !isLoading && (
                  <Grid item data-testid="app-card-cost">
                    <ContainedText>{cost}</ContainedText>
                  </Grid>
                )}

                {isLoading && (
                  <>
                    <Grid item>
                      <Skeleton
                        variant="rounded"
                        data-testid="app-card-cost-loading"
                      >
                        <ContainedText>Absolutely free</ContainedText>
                      </Skeleton>
                    </Grid>
                    <Grid item xs={12} container spacing={2}>
                      <Grid item xs={4}>
                        <Skeleton
                          variant="rounded"
                          height={68}
                          data-testid="app-card-platform-loading"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Skeleton
                          variant="rounded"
                          height={68}
                          data-testid="app-card-platform-loading"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Skeleton
                          variant="rounded"
                          height={68}
                          data-testid="app-card-platform-loading"
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {platforms &&
                  (appPlatformType === 'Threshold' ? (
                    <AppPlatformImageInfo
                      platforms={platforms}
                      isLoading={isLoading}
                      FlexibleImageComponent={FlexibleImageComponent}
                      thresholds={platformConfiguration?.thresholds}
                    />
                  ) : (
                    <AppPlatformInfo
                      platforms={platforms}
                      isLoading={isLoading}
                    />
                  ))}
              </Grid>
            </CardContent>
          </Collapse>
          {isCardOpen && <Divider light />}
          <CardFooterCollapsible
            handleCollapseClick={handleOpenClick}
            isCollapsed={isCardOpen}
            status={appStatusProps?.status}
          >
            <CardContent sx={{ paddingBottom: '8px !important' }}>
              <CardScreenshots
                appImages={appImages as any}
                isLoading={isLoading as any}
              />
            </CardContent>
          </CardFooterCollapsible>
        </CardActionArea>
        {appStatusProps && <CardFooterOverlay {...appStatusProps} />}
      </StyledCard>
      {assured && FlexibleImageComponent && (
        <ShieldImage>
          <FlexibleImageComponent
            width={50}
            height={56}
            src={assured.imageUrl}
            alt="Assured Badge"
          />
        </ShieldImage>
      )}
    </div>
  );
};

const ErrorFallback = () => {
  return <AssureAppCard />;
};

export const AppCardWithErrorBoundary = (props: AppCardProps) => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <AssureAppCard {...props} />
    </ErrorBoundary>
  );
};
